import React, { useEffect } from 'react'
import { getOwnerId } from '../utils'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import _ from 'lodash'
import { Form } from '@/features/shadcn/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import Combobox from '@/features/forms/Combobox'
import { Button } from '@/features/shadcn/ui/button'
import { LoadingState } from '../Traffic'
import {
  fetchGoogleAdsMetricsSummary,
  selectGoogleAdsAuditData,
  selectGoogleAdsMetricsSummary,
  selectGoogleAdsMetricsSummaryStatus,
  selectGoogleAdsApiError,
  fetchFbAdsMetricsSummary,
  selectFbAdsAuditData,
  selectFbAdsMetricsSummary,
  selectFbAdsMetricsSummaryStatus,
  selectFbAdsApiError,
  selectKlaviyoAdsMetricsSummary,
  selectKlaviyoAdsMetricsSummaryStatus,
  selectKlaviyoAdsAuditData,
  selectKlaviyoApiError,
  fetchKlaviyoAdsMetricsSummary,
  selectPinterestAdsMetricsSummary,
  selectPinterestAdsMetricsSummaryStatus,
  selectPinterestAdsAuditData,
  selectPinterestApiError,
  fetchPinterestAdsMetricsSummary,
  selectTiktokAdsMetricsSummary,
  selectTiktokAdsMetricsSummaryStatus,
  selectTiktokAdsAuditData,
  selectTiktokApiError,
  fetchTiktokAdsMetricsSummary,
  selectAmazonAdsMetricsSummary,
  selectAmazonAdsMetricsSummaryStatus,
  selectAmazonAdsAuditData,
  selectAmazonApiError,
  fetchAmazonAdsMetricsSummary,
  selectUnmatchedAdsMetricsSummary,
  selectUnmatchedAdsMetricsSummaryStatus,
  fetchUnmatchedAdsMetricsSummary,
  resetFetchGoogleAdsMetricsSummaryStatus,
  resetFetchFbAdsMetricsSummaryStatus,
  resetFetchKlaviyoAdsMetricsSummaryStatus,
  resetFetchPinterestAdsMetricsSummaryStatus,
  resetFetchTiktokAdsMetricsSummaryStatus,
  resetFetchAmazonAdsMetricsSummaryStatus,
  resetFetchUnmatchedAdsMetricsSummaryStatus,
} from '../trafficReportSlice'
import {
  selectAttributionSettings,
  setAdPlatformOptions,
  selectSaveStatus, // Add this selector
} from '../trafficSettingSlice'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import AuditModal from './AuditModal'
import MetricBox from './MetricBox'
import { selectRootAccounts, selectRootUser } from '../../../rootSlice'
import { useIntegrations, useCategories } from '../../Integrations/hooks'

function AdsTab({ prevTimePeriod, timePeriodWatch, customDateRangeWatch, filtersWatch }) {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const qs = queryString.parse(location.search)

  const FormSchema = z.object({
    adPlatform: z.string({
      required_error: 'Please select an ad platform.',
    }),
  })

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      adPlatform: 'summary',
    },
  })

  const adPlatformWatch = form.watch('adPlatform')


  /**
   * Get Ad Accounts and format ad platform options
   * @type {Array<{label: string, value: string}>}
   */
  const accounts = useSelector(selectRootAccounts)
  const ownerId = useSelector(selectRootUser)?.id
  const { data: integrations } = useIntegrations({ accounts, ownerId })
  const { data: categories } = useCategories({ integrationIds: integrations.ids })
  const selectedCategory = categories.entities['all']

  const selectedCategoryIntegrations = React.useMemo(() => {
    return selectedCategory.integrationIds && selectedCategory.integrationIds.map((_id) => integrations.entities[_id]).filter(v => v)
  }, [selectedCategory.integrationIds, integrations.entities])

  const connectedIntegrations = React.useMemo(() => {
    return selectedCategoryIntegrations &&
    selectedCategoryIntegrations.filter(({ connected }) => connected)
  }, [selectedCategoryIntegrations])

  const acceptedAdPlatforms = ['Google', 'Facebook Ads', 'Google Ads', 'Klaviyo', 'Pinterest', 'TikTok', 'Amazon Ads']

  const AdAccounts = React.useMemo(() => {
    if (!connectedIntegrations) return []
    return connectedIntegrations.filter((account) => acceptedAdPlatforms.includes(account.channel))
  }, [connectedIntegrations])

  const adPlatformOptions = React.useMemo(() => {
    return [..._.uniqBy(AdAccounts.map((account) => ({
      label: account.channel === 'Google' ? 'Google Ads' : account.channel,
      value: account.channel === 'Google' ? 'google_ads' : account.channel === 'Amazon Ads' ? 'amazon' : account.id,
    })), 'value'), {
      label: 'Summary',
      value: 'summary',
    }]
  }, [AdAccounts])

  // Create stable boolean flags for available ad platforms to use in useEffect
  const availablePlatforms = React.useMemo(() => {
    const hasGoogle = adPlatformOptions.some(account => account.label === 'Google' || account.label === 'Google Ads')
    const hasFacebook = adPlatformOptions.some(account => account.label === 'Facebook Ads')
    const hasKlaviyo = adPlatformOptions.some(account => account.label === 'Klaviyo')
    const hasPinterest = adPlatformOptions.some(account => account.label === 'Pinterest')
    const hasTikTok = adPlatformOptions.some(account => account.label === 'TikTok')
    const hasAmazon = adPlatformOptions.some(account => account.label === 'Amazon Ads')

    return {
      google: hasGoogle,
      facebook: hasFacebook,
      klaviyo: hasKlaviyo,
      pinterest: hasPinterest,
      tiktok: hasTikTok,
      amazon: hasAmazon,
    }
  }, [adPlatformOptions])

  // Create a stable string representation for useEffect dependency
  const availablePlatformsKey = React.useMemo(() => {
    return JSON.stringify(availablePlatforms)
  }, [availablePlatforms])

  /**
   *
   * Ads metrics data
   *
   */
  const googleAdsMetricsSummaryData = useSelector(selectGoogleAdsMetricsSummary)
  const googleAdsMetricsSummaryStatus = useSelector(selectGoogleAdsMetricsSummaryStatus)
  const googleAdsAuditData = useSelector(selectGoogleAdsAuditData)
  const googleAdsApiError = useSelector(selectGoogleAdsApiError)
  const fbAdsMetricsSummaryData = useSelector(selectFbAdsMetricsSummary)
  const fbAdsMetricsSummaryStatus = useSelector(selectFbAdsMetricsSummaryStatus)
  const fbAdsAuditData = useSelector(selectFbAdsAuditData)
  const fbAdsApiError = useSelector(selectFbAdsApiError)
  const klaviyoAdsMetricsSummaryData = useSelector(selectKlaviyoAdsMetricsSummary)
  const klaviyoAdsMetricsSummaryStatus = useSelector(selectKlaviyoAdsMetricsSummaryStatus)
  const klaviyoAdsAuditData = useSelector(selectKlaviyoAdsAuditData)
  const klaviyoApiError = useSelector(selectKlaviyoApiError)
  const pinterestAdsMetricsSummaryData = useSelector(selectPinterestAdsMetricsSummary)
  const pinterestAdsMetricsSummaryStatus = useSelector(selectPinterestAdsMetricsSummaryStatus)
  const pinterestAdsAuditData = useSelector(selectPinterestAdsAuditData)
  const pinterestApiError = useSelector(selectPinterestApiError)
  const tiktokAdsMetricsSummaryData = useSelector(selectTiktokAdsMetricsSummary)
  const tiktokAdsMetricsSummaryStatus = useSelector(selectTiktokAdsMetricsSummaryStatus)
  const tiktokAdsAuditData = useSelector(selectTiktokAdsAuditData)
  const tiktokApiError = useSelector(selectTiktokApiError)
  const amazonAdsMetricsSummaryData = useSelector(selectAmazonAdsMetricsSummary)
  const amazonAdsMetricsSummaryStatus = useSelector(selectAmazonAdsMetricsSummaryStatus)
  const amazonAdsAuditData = useSelector(selectAmazonAdsAuditData)
  const amazonApiError = useSelector(selectAmazonApiError)
  const unmatchedAdsMetricsSummaryData = useSelector(selectUnmatchedAdsMetricsSummary)
  const unmatchedAdsMetricsSummaryStatus = useSelector(selectUnmatchedAdsMetricsSummaryStatus)

  const attributionSettings = useSelector(selectAttributionSettings)
  const saveStatus = useSelector(selectSaveStatus)

  const [summaryMetricsStatus, setSummaryMetricsStatus] = React.useState('idle')

  useEffect(() => {
    dispatch(setAdPlatformOptions(adPlatformOptions))
  }, [adPlatformOptions])

  useEffect(() => {
    const fn = async () => {

      if (!adPlatformWatch) return

      const timePeriodChanged = !_.isEqual(timePeriodWatch, prevTimePeriod)
      const settingsJustSaved = saveStatus === 'fulfilled'
      if (!timePeriodWatch || _.isEmpty(timePeriodWatch) || (!timePeriodChanged && !settingsJustSaved)) return

      const _filters = qs.filters ? JSON.parse(qs.filters) : '' // [{ field: 'cartStatus', op: 'eq', value: 'abandoned' }]

      if (adPlatformWatch === 'google_ads') {
        if (googleAdsMetricsSummaryStatus === 'idle') {
          const _googleAdsMetricsSummaryData = await dispatch(
            fetchGoogleAdsMetricsSummary({
              timePeriod: timePeriodWatch,
              customDateRange: customDateRangeWatch,
              filters: _filters,
              attribution: attributionSettings?.google_ads,
            })
          ).unwrap()
          dispatch(resetFetchGoogleAdsMetricsSummaryStatus())
        }
      }

      if (adPlatformWatch === 'facebook_ads') {
        if (fbAdsMetricsSummaryStatus === 'idle') {
          const _fbAdsMetricsSummaryData = await dispatch(
            fetchFbAdsMetricsSummary({
              timePeriod: timePeriodWatch,
              customDateRange: customDateRangeWatch,
              filters: _filters,
              attribution: attributionSettings?.facebook_ads,
            })
          ).unwrap()
          dispatch(resetFetchFbAdsMetricsSummaryStatus())
        }
      }

      if (adPlatformWatch === 'klaviyo') {
        if (klaviyoAdsMetricsSummaryStatus === 'idle') {
          const _klaviyoAdsMetricsSummaryData = await dispatch(
            fetchKlaviyoAdsMetricsSummary({
              timePeriod: timePeriodWatch,
              customDateRange: customDateRangeWatch,
              filters: _filters,
              attribution: attributionSettings?.klaviyo,
            })
          ).unwrap()
          dispatch(resetFetchKlaviyoAdsMetricsSummaryStatus())
        }
      }

      if (adPlatformWatch === 'pinterest') {
        if (pinterestAdsMetricsSummaryStatus === 'idle') {
          const _pinterestAdsMetricsSummaryData = await dispatch(
            fetchPinterestAdsMetricsSummary({
              timePeriod: timePeriodWatch,
              customDateRange: customDateRangeWatch,
              filters: _filters,
              attribution: attributionSettings?.pinterest,
            })
          ).unwrap()
          dispatch(resetFetchPinterestAdsMetricsSummaryStatus())
        }
      }

      if (adPlatformWatch === 'tiktok') {
        if (tiktokAdsMetricsSummaryStatus === 'idle') {
          const _tiktokAdsMetricsSummaryData = await dispatch(
            fetchTiktokAdsMetricsSummary({
              timePeriod: timePeriodWatch,
              customDateRange: customDateRangeWatch,
              filters: _filters,
              attribution: attributionSettings?.tiktok,
            })
          ).unwrap()
          dispatch(resetFetchTiktokAdsMetricsSummaryStatus())
        }
      }

      if (adPlatformWatch === 'summary') {

        setSummaryMetricsStatus('loading')

        if (googleAdsMetricsSummaryStatus === 'idle' && availablePlatforms.google) {
          try {
            const googleAdsPromise = await dispatch(
              fetchGoogleAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
                attribution: attributionSettings?.google_ads,
              })
            ).unwrap();
            dispatch(resetFetchGoogleAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching Google Ads metrics summary')
          }
        }

        if (fbAdsMetricsSummaryStatus === 'idle' && availablePlatforms.facebook) {
          try {
            const fbAdsPromise = await dispatch(
              fetchFbAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
                attribution: attributionSettings?.facebook_ads,
              })
            ).unwrap();
            dispatch(resetFetchFbAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching Facebook Ads metrics summary')
          }
        }

        if (klaviyoAdsMetricsSummaryStatus === 'idle' && availablePlatforms.klaviyo) {
          try {
            const klaviyoAdsPromise = await dispatch(
              fetchKlaviyoAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
                attribution: attributionSettings?.klaviyo,
              }),
            ).unwrap();
            dispatch(resetFetchKlaviyoAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching Klaviyo Ads metrics summary')
          }
        }

        if (pinterestAdsMetricsSummaryStatus === 'idle' && availablePlatforms.pinterest) {
          try {
            const pinterestAdsPromise = await dispatch(
              fetchPinterestAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
                attribution: attributionSettings?.pinterest,
              }),
            ).unwrap();
            dispatch(resetFetchPinterestAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching Pinterest Ads metrics summary')
          }
        }

        if (tiktokAdsMetricsSummaryStatus === 'idle' && availablePlatforms.tiktok) {
          try {
            const tiktokAdsPromise = await dispatch(
              fetchTiktokAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
                attribution: attributionSettings?.tiktok,
              })
            ).unwrap();
            dispatch(resetFetchTiktokAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching TikTok Ads metrics summary')
          }
        }

        if (amazonAdsMetricsSummaryStatus === 'idle' && availablePlatforms.amazon) {
          try {
            const amazonAdsPromise = await dispatch(
              fetchAmazonAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
                attribution: attributionSettings?.amazon,
              })
            ).unwrap();
            dispatch(resetFetchAmazonAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching Amazon Ads metrics summary')
          }
        }

        if (unmatchedAdsMetricsSummaryStatus === 'idle') {
          try {
            const unmatchedAdsPromise = await dispatch(
              fetchUnmatchedAdsMetricsSummary({
                timePeriod: timePeriodWatch,
                customDateRange: customDateRangeWatch,
                filters: _filters,
              })
            ).unwrap()
            dispatch(resetFetchUnmatchedAdsMetricsSummaryStatus())
          } catch (error) {
            console.error('Error fetching unmatched ads metrics summary')
          }
        }

        setSummaryMetricsStatus('idle')
      }
    }
    fn()
  }, [adPlatformWatch, prevTimePeriod, timePeriodWatch, saveStatus, availablePlatformsKey])

  /**
   *
   * Set default values
   *
   */
  // useEffect(() => {
  //   form.reset({
  //     // adPlatform: adPlatformOptions[0].value,
  //     adPlatform: 'summary',
  //   })
  // }, [])

  const onSubmit = async (values) => {
    const _filters = filtersWatch.map((f) => ({ ...f, op: f.op || 'eq' }))
    if (values.adPlatform === 'google_ads') {
      const _googleAdsMetricsSummaryData = await dispatch(
        fetchGoogleAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
          attribution: attributionSettings?.google_ads,
        })
      ).unwrap()
    }

    if (values.adPlatform === 'facebook_ads') {
      const _fbAdsMetricsSummaryData = await dispatch(
        fetchFbAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
          attribution: attributionSettings?.facebook_ads,
        })
      ).unwrap()
    }

    if (values.adPlatform === 'klaviyo') {
      const _klaviyoAdsMetricsSummaryData = await dispatch(
        fetchKlaviyoAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
          attribution: attributionSettings?.klaviyo,
        }),
      ).unwrap()
    }

    if (values.adPlatform === 'pinterest') {
      const _pinterestAdsMetricsSummaryData = await dispatch(
        fetchPinterestAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
          attribution: attributionSettings?.pinterest,
        }),
      ).unwrap()
    }

    if (values.adPlatform === 'tiktok') {
      const _tiktokAdsMetricsSummaryData = await dispatch(
        fetchTiktokAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
          attribution: attributionSettings?.tiktok,
        })
      ).unwrap()
    }

    if (values.adPlatform === 'amazon') {
      const _amazonAdsMetricsSummaryData = await dispatch(
        fetchAmazonAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
          attribution: attributionSettings?.amazon,
        }),
      ).unwrap()
    }

    if (values.adPlatform === 'summary') {

      setSummaryMetricsStatus('loading')

      if (availablePlatforms.google) {
        const googleAdsPromise = await dispatch(
          fetchGoogleAdsMetricsSummary({
            timePeriod: timePeriodWatch,
            customDateRange: customDateRangeWatch,
            filters: _filters,
            attribution: attributionSettings?.google_ads,
          })
        ).unwrap();
      }

      if (availablePlatforms.facebook) {
        const fbAdsPromise = await dispatch(
          fetchFbAdsMetricsSummary({
            timePeriod: timePeriodWatch,
            customDateRange: customDateRangeWatch,
            filters: _filters,
            attribution: attributionSettings?.facebook_ads,
          })
        ).unwrap();
      }

      if (availablePlatforms.klaviyo) {
        const klaviyoAdsPromise = await dispatch(
          fetchKlaviyoAdsMetricsSummary({
            timePeriod: timePeriodWatch,
            customDateRange: customDateRangeWatch,
            filters: _filters,
            attribution: attributionSettings?.klaviyo,
          }),
        ).unwrap();
      }

      if (availablePlatforms.pinterest) {
        const pinterestAdsPromise = await dispatch(
          fetchPinterestAdsMetricsSummary({
            timePeriod: timePeriodWatch,
            customDateRange: customDateRangeWatch,
            filters: _filters,
            attribution: attributionSettings?.pinterest,
          }),
        ).unwrap();
      }

      if (availablePlatforms.tiktok) {
        const tiktokAdsPromise = await dispatch(
          fetchTiktokAdsMetricsSummary({
            timePeriod: timePeriodWatch,
            customDateRange: customDateRangeWatch,
            filters: _filters,
            attribution: attributionSettings?.tiktok,
          })
        ).unwrap();
      }

      if (availablePlatforms.amazon) {
        const amazonAdsPromise = await dispatch(
          fetchAmazonAdsMetricsSummary({
            timePeriod: timePeriodWatch,
            customDateRange: customDateRangeWatch,
            filters: _filters,
            attribution: attributionSettings?.amazon,
          }),
        ).unwrap();
      }

      const unmatchedAdsPromise = await dispatch(
        fetchUnmatchedAdsMetricsSummary({
          timePeriod: timePeriodWatch,
          customDateRange: customDateRangeWatch,
          filters: _filters,
        })
      ).unwrap();

      setSummaryMetricsStatus('idle');
    }
  }

  /**
   *
   * Handle audit
   *
   */
  const [auditOpen, setAuditOpen] = React.useState(false)
  const [fbAuditOpen, setfbAuditOpen] = React.useState(false)
  const [klaviyoAuditOpen, setKlaviyoAuditOpen] = React.useState(false)
  const [pinterestAuditOpen, setPinterestAuditOpen] = React.useState(false)
  const [tiktokAuditOpen, setTiktokAuditOpen] = React.useState(false)
  const [amazonAuditOpen, setAmazonAuditOpen] = React.useState(false)
  const [summaryAuditOpen, setSummaryAuditOpen] = React.useState(false)
  const handleAuditOpen = async () => {
    if (adPlatformWatch === 'google_ads') {
      setAuditOpen(true)
    }

    if (adPlatformWatch == 'facebook_ads') {
      setfbAuditOpen(true)
    }

    if (adPlatformWatch == 'klaviyo') {
      setKlaviyoAuditOpen(true)
    }

    if (adPlatformWatch == 'pinterest') {
      setPinterestAuditOpen(true)
    }

    if (adPlatformWatch == 'tiktok') {
      setTiktokAuditOpen(true)
    }

    if (adPlatformWatch == 'amazon') {
      setAmazonAuditOpen(true)
    }

    if (adPlatformWatch == 'summary') {
      setSummaryAuditOpen(true)
    }
  }

  const tableColumns = [
    {
      accessorKey: 'campaign_payhelm',
      header: 'Campaign Data in Payhelm',
      cell: ({ row }) => {
        const value = row.getValue('campaign_payhelm')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        return <div className="tw-max-w-48 tw-text-left">{value}</div>
      },
    },
    {
      accessorKey: 'campaign_google_ads',
      header: 'Campaign Data in Google Ads',
      cell: ({ row }) => {
        const value = row.getValue('campaign_google_ads')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        const campaignId = value?.split('(id: ')[1].replace(')', '')
        return (
          <div className="tw-max-w-48 tw-text-left">
            <a href={`https://ads.google.com/aw/campaigns?ocid=${campaignId}`} target="_blank">
              {value}
            </a>
          </div>
        )
      },
    },
  ]
  const [tableRows, setTableRows] = React.useState([])
  const [fbtableRows, setfbTableRows] = React.useState([])
  const [klaviyoTableRows, setKlaviyoTableRows] = React.useState([])
  const [pinterestTableRows, setPinterestTableRows] = React.useState([])
  const [tiktokTableRows, setTiktokTableRows] = React.useState([])
  const [amazonTableRows, setAmazonTableRows] = React.useState([])
  useEffect(() => {
    const mismatchingCampaignsInTrafficDBData =
      googleAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0
        ? [
            ...googleAdsAuditData.missingCampaigns.mismatchingCampaignsInTrafficDBData.map((campaign) => {
              return {
                campaign_payhelm: `${campaign.utm_campaign} (id: ${campaign.utm_id})`,
                campaign_google_ads: 'Missing',
                // campaign_google_ads: 'Missing in connected Google Ads accounts',
              }
            }),
          ]
        : []

    const mismatchingCampaignsInGoogleAdsAPI =
      googleAdsAuditData?.missingCampaigns?.mismatchingCampaignsInGoogleAdsAPI?.length > 0
        ? [
            ...googleAdsAuditData.missingCampaigns.mismatchingCampaignsInGoogleAdsAPI.map((campaign) => {
              return {
                campaign_payhelm: 'Missing',
                // campaign_payhelm: 'Missing in Payhelm',
                campaign_google_ads: `${campaign.campaign.name} (id: ${campaign.campaign.id})`,
              }
            }),
          ]
        : []

    setTableRows([...mismatchingCampaignsInTrafficDBData, ...mismatchingCampaignsInGoogleAdsAPI])
  }, [googleAdsAuditData])

  useEffect(() => {
    const mismatchingCampaignsInTrafficDBData =
      fbAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0
        ? [
            ...fbAdsAuditData.missingCampaigns.mismatchingCampaignsInTrafficDBData.map((campaign) => {
              return {
                campaign_payhelm: `${campaign.utm_campaign} (id: ${campaign.utm_id})`,
                campaign_facebook_ads: 'Missing',
                // campaign_google_ads: 'Missing in connected Google Ads accounts',
              }
            }),
          ]
        : []

    const mismatchingCampaignsInFbAdsAPI =
      fbAdsAuditData?.missingCampaigns?.mismatchingCampaignsInFbAdsAPI?.length > 0
        ? [
            ...fbAdsAuditData.missingCampaigns.mismatchingCampaignsInFbAdsAPI.map((campaign) => {
              return {
                campaign_payhelm: 'Missing',
                // campaign_payhelm: 'Missing in Payhelm',
                campaign_facebook_ads: `${campaign.name} (id: ${campaign.id}) (act_id: ${campaign.account_id})`,
              }
            }),
          ]
        : []

      setfbTableRows([...mismatchingCampaignsInTrafficDBData, ...mismatchingCampaignsInFbAdsAPI])
  }, [fbAdsAuditData])

  const fbtableColumns = [
    {
      accessorKey: 'campaign_payhelm',
      header: 'Campaign Data in Payhelm',
      cell: ({ row }) => {
        const value = row.getValue('campaign_payhelm')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        return <div className="tw-max-w-48 tw-text-left">{value}</div>
      },
    },
    {
      accessorKey: 'campaign_facebook_ads',
      header: 'Campaign Data in Facebook Ads',
      cell: ({ row }) => {
        const value = row.getValue('campaign_facebook_ads')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        const campaignId = value?.split('(id: ')[1].replace(')', '')
        const acctId = value?.split('(act_id: ')[1].replace(')', '')
        return (
          <div className="tw-max-w-48 tw-text-left">
            <a href={`https://www.facebook.com/adsmanager/manage/campaigns?act=${acctId}&campaign_id=${campaignId}`} target="_blank">
              {value}
            </a>
          </div>
        )
      },
    },
  ]

  useEffect(() => {
    const mismatchingCampaignsInTrafficDBData =
      klaviyoAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0
        ? [
            ...klaviyoAdsAuditData.missingCampaigns.mismatchingCampaignsInTrafficDBData.map((campaign) => {
              return {
                campaign_payhelm: `${campaign.utm_campaign} (id: ${campaign.utm_id ? campaign.utm_id : 'Not found'})`,
                campaign_klaviyo: 'Missing',
                // campaign_google_ads: 'Missing in connected Google Ads accounts',
              }
            }),
          ]
        : []

    const mismatchingCampaignsInKlaviyoAdsAPI =
      klaviyoAdsAuditData?.missingCampaigns?.mismatchingCampaignsInKlaviyoAPI?.length > 0
        ? [
            ...klaviyoAdsAuditData.missingCampaigns.mismatchingCampaignsInKlaviyoAPI.map((campaign) => {
              return {
                campaign_payhelm: 'Missing',
                // campaign_payhelm: 'Missing in Payhelm',
                campaign_klaviyo: `${campaign.by === '$flow' ? '(Flow ID)' : ''} ${campaign.byId}`,
              }
            }),
          ]
        : []

      setKlaviyoTableRows([...mismatchingCampaignsInTrafficDBData, ...mismatchingCampaignsInKlaviyoAdsAPI])
  }, [klaviyoAdsAuditData])

  const klaviyoTableColumns = [
    {
      accessorKey: 'campaign_payhelm',
      header: 'Campaign Data in Payhelm',
      cell: ({ row }) => {
        const value = row.getValue('campaign_payhelm')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        return <div className="tw-max-w-48 tw-text-left">{value}</div>
      },
    },
    {
      accessorKey: 'campaign_klaviyo',
      header: 'Campaign Data in Klaviyo',
      cell: ({ row }) => {
        const value = row.getValue('campaign_klaviyo')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        const campaignId = value.replace('(Flow ID)', '').trim()
        return (
          <div className="tw-max-w-48 tw-text-left">
            <a href={`https://www.klaviyo.com/campaigns/${encodeURIComponent(campaignId)}`} target="_blank">
              {value}
            </a>
          </div>
        )
      },
    },
  ]

  useEffect(() => {
    const mismatchingCampaignsInTrafficDBData =
      pinterestAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0
        ? [
            ...pinterestAdsAuditData.missingCampaigns.mismatchingCampaignsInTrafficDBData.map((campaign) => {
              return {
                campaign_payhelm: `${campaign.utm_campaign} (id: ${campaign.utm_id ? campaign.utm_id : 'Not found'})`,
                campaign_pinterest: 'Missing',
              }
            }),
          ]
        : []

    const mismatchingCampaignsInPinterestAdsAPI =
      pinterestAdsAuditData?.missingCampaigns?.mismatchingCampaignsInPinterestAPI?.length > 0
        ? [
            ...pinterestAdsAuditData.missingCampaigns.mismatchingCampaignsInPinterestAPI.map((campaign) => {
              return {
                campaign_payhelm: 'Missing',
                campaign_pinterest: `${campaign.name} (id: ${campaign.id})`,
              }
            }),
          ]
        : []

      setPinterestTableRows([...mismatchingCampaignsInTrafficDBData, ...mismatchingCampaignsInPinterestAdsAPI])
  }, [pinterestAdsAuditData])

  useEffect(() => {
    const mismatchingCampaignsInTrafficDBData =
      tiktokAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0
        ? [
            ...tiktokAdsAuditData.missingCampaigns.mismatchingCampaignsInTrafficDBData.map((campaign) => {
              return {
                campaign_payhelm: `${campaign.utm_campaign} (id: ${campaign.utm_id ? campaign.utm_id : 'Not found'})`,
                campaign_tiktok: 'Missing',
              }
            }),
          ]
        : []

    const mismatchingCampaignsInTiktokAdsAPI =
      tiktokAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTiktokAPI?.length > 0
        ? [
            ...tiktokAdsAuditData.missingCampaigns.mismatchingCampaignsInTiktokAPI.map((campaign) => {
              return {
                campaign_payhelm: 'Missing',
                campaign_tiktok: `${campaign.name} (id: ${campaign.id})`,
              }
            }),
          ]
        : []

      setTiktokTableRows([...mismatchingCampaignsInTrafficDBData, ...mismatchingCampaignsInTiktokAdsAPI])
  }, [tiktokAdsAuditData])

  useEffect(() => {
    const mismatchingCampaignsInTrafficDBData =
      amazonAdsAuditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0
        ? [
            ...amazonAdsAuditData.missingCampaigns.mismatchingCampaignsInTrafficDBData.map((campaign) => {
              return {
                campaign_payhelm: `${campaign.utm_campaign} (id: ${campaign.utm_id ? campaign.utm_id : 'Not found'})`,
                campaign_amazon: 'Missing',
              }
            }),
          ]
        : []

    const mismatchingCampaignsInAmazonAdsAPI =
      amazonAdsAuditData?.missingCampaigns?.mismatchingCampaignsInAmazonAPI?.length > 0
        ? [
            ...amazonAdsAuditData.missingCampaigns.mismatchingCampaignsInAmazonAPI.map((campaign) => {
              return {
                campaign_payhelm: 'Missing',
                campaign_amazon: `${campaign.name} (id: ${campaign.id})`,
              }
            }),
          ]
        : []

      setAmazonTableRows([...mismatchingCampaignsInTrafficDBData, ...mismatchingCampaignsInAmazonAdsAPI])
  }, [amazonAdsAuditData])

  const pinterestTableColumns = [
    {
      accessorKey: 'campaign_payhelm',
      header: 'Campaign Data in Payhelm',
      cell: ({ row }) => {
        const value = row.getValue('campaign_payhelm')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        return <div className="tw-max-w-48 tw-text-left">{value}</div>
      },
    },
    {
      accessorKey: 'campaign_pinterest',
      header: 'Campaign Data in Pinterest',
      cell: ({ row }) => {
        const value = row.getValue('campaign_pinterest')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        const campaignId = value?.split('(id: ')[1]?.replace(')', '')
        return (
          <div className="tw-max-w-48 tw-text-left">
            <a href={`https://ads.pinterest.com/campaigns/${campaignId}`} target="_blank">
              {value}
            </a>
          </div>
        )
      },
    },
  ]

  const tiktokTableColumns = [
    {
      accessorKey: 'campaign_payhelm',
      header: 'Campaign Data in Payhelm',
      cell: ({ row }) => {
        const value = row.getValue('campaign_payhelm')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        return <div className="tw-max-w-48 tw-text-left">{value}</div>
      },
    },
    {
      accessorKey: 'campaign_tiktok',
      header: 'Campaign Data in TikTok',
      cell: ({ row }) => {
        const value = row.getValue('campaign_tiktok')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        const campaignId = value?.split('(id: ')[1]?.replace(')', '')
        return (
          <div className="tw-max-w-48 tw-text-left">
            <a href={`https://ads.tiktok.com/i18n/campaign/edit/${campaignId}`} target="_blank">
              {value}
            </a>
          </div>
        )
      },
    },
  ]

  const amazonTableColumns = [
    {
      accessorKey: 'campaign_payhelm',
      header: 'Campaign Data in Payhelm',
      cell: ({ row }) => {
        const value = row.getValue('campaign_payhelm')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        return <div className="tw-max-w-48 tw-text-left">{value}</div>
      },
    },
    {
      accessorKey: 'campaign_amazon',
      header: 'Campaign Data in Amazon',
      cell: ({ row }) => {
        const value = row.getValue('campaign_amazon')
        if (value === 'Missing') return <div className="tw-max-w-48 tw-text-left tw-text-red-500">{value}</div>
        const campaignId = value?.split('(id: ')[1]?.replace(')', '')
        return (
          <div className="tw-max-w-48 tw-text-left">
            <a href={`https://advertising.amazon.com/campaigns/${campaignId}`} target="_blank">
              {value}
            </a>
          </div>
        )
      },
    },
  ]

  const [auditTipOpen, setAuditTipOpen] = React.useState(false)
  const toggleAuditTip = () => setAuditTipOpen(!auditTipOpen)

  const summaryMetricsData = (googleAdsMetricsSummaryData || fbAdsMetricsSummaryData || klaviyoAdsMetricsSummaryData || pinterestAdsMetricsSummaryData || tiktokAdsMetricsSummaryData || amazonAdsMetricsSummaryData || unmatchedAdsMetricsSummaryData) ? _.groupBy([...(googleAdsMetricsSummaryData || []), ...(fbAdsMetricsSummaryData || []), ...(klaviyoAdsMetricsSummaryData || []), ...(pinterestAdsMetricsSummaryData || []), ...(tiktokAdsMetricsSummaryData || []), ...(amazonAdsMetricsSummaryData || []), ...(unmatchedAdsMetricsSummaryData || [])], 'channel') : []
  const summaryApiError = {
    'google_ads': googleAdsApiError,
    'facebook_ads': fbAdsApiError,
    'klaviyo': klaviyoApiError,
    'pinterest': pinterestApiError,
    'tiktok': tiktokApiError,
    'amazon': amazonApiError,
  }

  const adPlatformDataMap = {
    google_ads: {
      metricsSummaryData: googleAdsMetricsSummaryData,
      metricsSummaryStatus: googleAdsMetricsSummaryStatus,
      apiError: googleAdsApiError,
      auditData: googleAdsAuditData,
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen,
        setAuditOpen,
        adPlatformOptions,
        tableColumns,
        tableRows,
        adPlatformWatch,
        hasMismatchingTimezones: googleAdsAuditData?.hasMatchingTimeZone === false,
      },
    },
    facebook_ads: {
      metricsSummaryData: fbAdsMetricsSummaryData,
      metricsSummaryStatus: fbAdsMetricsSummaryStatus,
      apiError: fbAdsApiError,
      auditData: fbAdsAuditData,
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen: fbAuditOpen,
        setAuditOpen: setfbAuditOpen,
        adPlatformOptions,
        tableColumns: fbtableColumns,
        tableRows: fbtableRows,
        adPlatformWatch,
        hasMismatchingTimezones: fbAdsAuditData?.hasMatchingTimeZone === false,
      }
    },
    klaviyo: {
      metricsSummaryData: klaviyoAdsMetricsSummaryData,
      metricsSummaryStatus: klaviyoAdsMetricsSummaryStatus,
      apiError: klaviyoApiError,
      auditData: klaviyoAdsAuditData,
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen: klaviyoAuditOpen,
        setAuditOpen: setKlaviyoAuditOpen,
        adPlatformOptions,
        tableColumns: klaviyoTableColumns,
        tableRows: klaviyoTableRows,
        adPlatformWatch,
        hasMismatchingTimezones: klaviyoAdsAuditData?.hasMatchingTimeZone === false,
      },
    },
    pinterest: {
      metricsSummaryData: pinterestAdsMetricsSummaryData,
      metricsSummaryStatus: pinterestAdsMetricsSummaryStatus,
      apiError: pinterestApiError,
      auditData: pinterestAdsAuditData,
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen: pinterestAuditOpen,
        setAuditOpen: setPinterestAuditOpen,
        adPlatformOptions,
        tableColumns: pinterestTableColumns,
        tableRows: pinterestTableRows,
        adPlatformWatch,
        hasMismatchingTimezones: pinterestAdsAuditData?.hasMatchingTimeZone === false,
      },
    },
    tiktok: {
      metricsSummaryData: tiktokAdsMetricsSummaryData,
      metricsSummaryStatus: tiktokAdsMetricsSummaryStatus,
      apiError: tiktokApiError,
      auditData: tiktokAdsAuditData,
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen: tiktokAuditOpen,
        setAuditOpen: setTiktokAuditOpen,
        adPlatformOptions,
        tableColumns: tiktokTableColumns,
        tableRows: tiktokTableRows,
        adPlatformWatch,
        hasMismatchingTimezones: tiktokAdsAuditData?.hasMatchingTimeZone === false,
      },
    },
    amazon: {
      metricsSummaryData: amazonAdsMetricsSummaryData,
      metricsSummaryStatus: amazonAdsMetricsSummaryStatus,
      apiError: amazonApiError,
      auditData: amazonAdsAuditData,
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen: amazonAuditOpen,
        setAuditOpen: setAmazonAuditOpen,
        adPlatformOptions,
        tableColumns: amazonTableColumns,
        tableRows: amazonTableRows,
        adPlatformWatch,
        hasMismatchingTimezones: amazonAdsAuditData?.hasMatchingTimeZone === false,
      },
    },
    summary: {
      metricsSummaryData: summaryMetricsData,
      metricsSummaryStatus: _.some(
        [summaryMetricsStatus, klaviyoAdsMetricsSummaryStatus, fbAdsMetricsSummaryStatus, pinterestAdsMetricsSummaryStatus, tiktokAdsMetricsSummaryStatus],
        status => status === 'loading'
      ) ? 'loading' : 'idle',
      auditData: {
        ...fbAdsAuditData,
        ...googleAdsAuditData,
        ...klaviyoAdsAuditData,
        ...pinterestAdsAuditData,
        ...tiktokAdsAuditData,
        ...amazonAdsAuditData
      },
      auditProps: {
        toggleAuditTip,
        auditTipOpen,
        auditOpen: summaryAuditOpen,
        setAuditOpen: setSummaryAuditOpen,
        adPlatformOptions,
        tableColumns: {
          facebook_ads: fbtableColumns,
          google_ads: tableColumns,
          klaviyo: klaviyoTableColumns,
          pinterest: pinterestTableColumns,
          tiktok: tiktokTableColumns,
          amazon: amazonTableColumns,
        },
        tableRows: {
          facebook_ads: fbtableRows,
          google_ads: tableRows,
          klaviyo: klaviyoTableRows,
          pinterest: pinterestTableRows,
          tiktok: tiktokTableRows,
          amazon: amazonTableRows,
        },
        adPlatformWatch,
      },
      apiError: summaryApiError,
    }
  }

  const selectedAdPlatformData = adPlatformDataMap[adPlatformWatch] || {}

  const {
    metricsSummaryData,
    metricsSummaryStatus,
    auditData,
    auditProps,
    apiError,
  } = selectedAdPlatformData

  const showAuditMessage = ((auditData?.missingCampaigns?.mismatchingCampaignsInTrafficDBData?.length > 0 ||
    auditData?.missingCampaigns?.mismatchingCampaignsInGoogleAdsAPI?.length > 0 ||
    auditData?.missingCampaigns?.mismatchingCampaignsInFbAdsAPI?.length > 0 ||
    auditData?.missingCampaigns?.mismatchingCampaignsInKlaviyoAPI?.length > 0 ||
    auditData?.missingCampaigns?.mismatchingCampaignsInPinterestAPI?.length > 0 ||
    auditData?.missingCampaigns?.mismatchingCampaignsInTiktokAPI?.length > 0 ||
    auditData?.missingCampaigns?.mismatchingCampaignsInAmazonAPI?.length > 0) && metricsSummaryStatus !== 'loading')

  const showIntegrationMessage = adPlatformOptions.length < 1
  const currentTab = adPlatformOptions.find(({ value }) => value === adPlatformWatch)?.label

  return (
    <>
      <div className="tw-mt-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="tw-w-full md:tw-w-[540px] tw-flex tw-items-center tw-space-x-4 tw-mb-6"
          >
            <div className="tw-grow tw-grid tw-gap-4 tw-grid-cols-1 md:tw-grid-cols-12">
              <div className="tw-col-span-1 md:tw-col-span-5">
                <div className="tw-w-full">
                  <Combobox
                    form={form}
                    name="adPlatform"
                    label="Ad Platform"
                    labelClassName="!tw-font-bold"
                    placeholder="Select ad platform"
                    options={adPlatformOptions}
                  />
                </div>
              </div>
              <div className="tw-col-span-1 md:tw-col-span-2">
                <Button type="submit" className="md:tw-mt-[20px] !tw-h-9">
                  Retrieve Data
                </Button>
              </div>
            </div>
          </form>
        </Form>

        {showIntegrationMessage && (
          <div className="tw-rounded-lg tw-border tw-border-red-400 tw-bg-red-50 tw-px-4 tw-py-3 tw-mb-6">
            <div className="tw-text-center tw-mt-4">
              <p className="tw-text-lg tw-font-semibold">No Ad Accounts Connected</p>
              <p className="tw-text-sm tw-text-gray-600">
                To use this tab, please go to{' '}
                <a href="/integration" className="tw-text-blue-500 tw-underline">
                  Integrations
                </a>{' '}
                and connect your ad accounts.
              </p>
            </div>
          </div>
        )}

        {showAuditMessage && (
          <div className="tw-rounded-lg tw-border tw-border-red-400 tw-bg-red-50 tw-px-4 tw-py-3 tw-mb-6">
            There's mismatching data. Please see the{' '}
            <a className="tw-cursor-pointer tw-blue-500 tw-underline" onClick={handleAuditOpen}>
              {currentTab || 'Ads'} Audit
            </a>{' '}
            for more details.
          </div>
        )}

        {/* {metricsSummaryStatus === 'loading'  (
          <div className="tw-col-span-1 md:tw-col-span-9 2xl:tw-col-span-10 tw-mb-4">
            <div className="tw-flex tw-items-center tw-space-x-1.5">
              <AiOutlineLoading3Quarters className="tw-animate-spin" />
              <div>Loading...</div>
            </div>
          </div>
        )}

        {(metricsSummaryStatus !== 'loading' && (!metricsSummaryData || _.isEmpty(metricsSummaryData))) ? (
          <div className="tw-col-span-1 md:tw-col-span-9 2xl:tw-col-span-10">
            <EmptyState />
          </div>
        ) : null}

        <MetricBox
          metricsSummaryData={metricsSummaryData}
          adPlatformOptions={adPlatformOptions}
          adPlatformWatch={adPlatformWatch}
        /> */}

        {metricsSummaryStatus === 'loading' ? (
          <div className="tw-col-span-1 md:tw-col-span-9 2xl:tw-col-span-10">
            <div className="tw-mb-4">
              <div className="tw-flex tw-items-center tw-space-x-1.5">
                <AiOutlineLoading3Quarters className="tw-animate-spin" />
                <div>Loading...</div>
              </div>
            </div>
          </div>
        ) : !metricsSummaryData || _.isEmpty(metricsSummaryData) ? null : (
          <MetricBox
            metricsSummaryData={metricsSummaryData}
            adPlatformOptions={adPlatformOptions}
            adPlatformWatch={adPlatformWatch}
            apiError={apiError}
          />
        )}
      </div>

      {!adPlatformWatch ? null : <AuditModal {...auditProps} />}
    </>
  )
}

export default AdsTab